<template>
  <div v-if="isOpen" class="modal-overlay" @click="closeModal">
    <div class="download-modal" @click.stop>
      <!-- Success state after registration -->
      <EmailConfirmationSuccess
        v-if="registrationSuccess"
        :email="registeredEmail"
        @close="closeModal"
        @go-to-login="goToLogin"
      />

      <!-- Downloading spinner state -->
      <div v-else-if="isPdfGenerating" class="flex flex-col items-center justify-center py-16 px-8 gap-4">
        <svg class="w-10 h-10 animate-spin text-primary" fill="none" viewBox="0 0 24 24">
          <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
          <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
        </svg>
        <p class="text-sm font-medium text-zinc-700">Preparing your download…</p>
      </div>

      <div v-else class="signup-section">
        <!-- Header -->
        <div class="signup-header">
          <img src="../assets/icons/cv-tool-gold.png" alt="cv-tool.com Logo" class="logo-image" />
          <button @click="closeModal" class="modal-close-btn">
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>

          <h2 class="signup-title">Applying to multiple firms?</h2>
          <p class="signup-subtitle">
            Save your profile once — generate a perfectly targeted CV for each application in seconds, for free.
          </p>
        </div>

        <!-- Error Alert -->
        <div v-if="error" class="bg-red-50 dark:bg-red-900 p-4 rounded-md mb-4 mx-8">
          <div class="flex">
            <div class="flex-shrink-0">
              <svg class="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd" />
              </svg>
            </div>
            <div class="ml-3">
              <h3 class="text-sm font-medium text-red-800 dark:text-red-200">{{ error }}</h3>
            </div>
          </div>
        </div>

        <!-- Registration form -->
        <form @submit.prevent="handleSignup" class="signup-form">
          <div class="form-row">
            <div class="form-group">
              <label for="signup-first-name" class="form-label">First Name</label>
              <input
                id="signup-first-name"
                v-model="signupForm.first_name"
                type="text"
                required
                class="form-input"
                placeholder="First name"
              />
            </div>
            <div class="form-group">
              <label for="signup-last-name" class="form-label">Last Name</label>
              <input
                id="signup-last-name"
                v-model="signupForm.last_name"
                type="text"
                required
                class="form-input"
                placeholder="Last name"
              />
            </div>
          </div>

          <div class="form-group">
            <label for="signup-email" class="form-label">Email</label>
            <input
              id="signup-email"
              v-model="signupForm.email"
              type="email"
              required
              class="form-input"
              placeholder="Enter your email"
            />
          </div>

          <div class="form-group">
            <label for="signup-password" class="form-label">Password</label>
            <input
              id="signup-password"
              v-model="signupForm.password"
              type="password"
              required
              class="form-input"
              placeholder="Create a password"
              minlength="6"
            />
          </div>

          <button type="submit" :disabled="isSigningUp" class="signup-btn">
            <span v-if="isSigningUp">
              <svg class="inline w-4 h-4 mr-2 animate-spin" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Creating account...
            </span>
            <span v-else>Save Profile &amp; Continue</span>
          </button>
        </form>

        <div class="benefits-list">
          <div class="benefit-item">
            <svg class="benefit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>Multiple targeted CV versions</span>
          </div>
          <div class="benefit-item">
            <svg class="benefit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>Access from any device</span>
          </div>
          <div class="benefit-item">
            <svg class="benefit-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
            </svg>
            <span>Never lose your progress</span>
          </div>
        </div>

        <!-- No thanks -->
        <div class="no-thanks-section">
          <button type="button" class="no-thanks-btn" @click="dismissWithoutSignup">
            No thanks, just download the PDF
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref } from 'vue'
import EmailConfirmationSuccess from './EmailConfirmationSuccess.vue'
import './shared-modal.css'

export default {
  name: 'DownloadModal',
  components: {
    EmailConfirmationSuccess
  },
  props: {
    isOpen: {
      type: Boolean,
      default: false
    },
    cvData: {
      type: Object,
      default: null
    },
    isPdfGenerating: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close', 'signup-success', 'download'],
  setup(props, { emit }) {
    const isSigningUp = ref(false)
    const error = ref('')
    const registrationSuccess = ref(false)
    const registeredEmail = ref('')
    
    const signupForm = ref({
      first_name: '',
      last_name: '',
      email: '',
      password: ''
    })

    const clearGuestStorage = () => {
      localStorage.removeItem('cv-tool.com_autosave')
      sessionStorage.removeItem('cv_parser_result')
    }

    const closeModal = () => {
      registrationSuccess.value = false
      registeredEmail.value = ''
      error.value = ''
      signupForm.value = { first_name: '', last_name: '', email: '', password: '' }
      emit('close')
    }

    const dismissWithoutSignup = () => {
      clearGuestStorage()
      emit('download')
      // Modal stays open; parent sets isPdfGenerating=true (shows spinner)
      // and closes the modal from its finally block when done
    }

    const handleSignup = async () => {
      try {
        isSigningUp.value = true
        error.value = ''
        
        const requestBody = {
          user: {
            first_name: signupForm.value.first_name,
            last_name: signupForm.value.last_name,
            email: signupForm.value.email,
            password: signupForm.value.password,
            password_confirmation: signupForm.value.password
          }
        }

        // Include CV data if it exists
        if (props.cvData) {
          requestBody.cv_data = props.cvData
        }
        
        const response = await fetch('/api/users', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-CSRF-Token': getCSRFToken(),
            'Accept': 'application/json'
          },
          body: JSON.stringify(requestBody)
        })

        const data = await response.json()
        
        if (data.success) {
          clearGuestStorage()
          emit('signup-success', data)
          setTimeout(() => {
            closeModal()
            window.location.href = '/dashboard'
          }, 500)
        } else {
          error.value = data.message || (data.errors && data.errors.join(', ')) || 'Registration failed'
        }
      } catch (err) {
        console.error('Signup error:', err)
        error.value = 'An error occurred. Please try again.'
      } finally {
        isSigningUp.value = false
      }
    }

    const goToLogin = () => {
      closeModal()
      window.location.href = '/login'
    }

    const getCSRFToken = () => {
      const meta = document.querySelector('meta[name="csrf-token"]')
      return meta ? meta.getAttribute('content') : null
    }

    return {
      signupForm,
      isSigningUp,
      error,
      registrationSuccess,
      registeredEmail,
      closeModal,
      dismissWithoutSignup,
      handleSignup,
      goToLogin
    }
  }
}
</script>

<style scoped>
/* Component-specific styles for DownloadModal */
.download-modal {
  background: white;
  border-radius: 16px;
  border: 1px solid #e4e4e7;
  max-width: 480px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  position: relative;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 1rem;
}

.logo-image {
  width: auto;
  height: 40px;
  margin: 0 auto 1.5rem;
  display: block;
}

.signup-section {
  padding-bottom: 1.5rem;
}

.no-thanks-section {
  padding: 1.25rem 2rem 0.5rem;
  text-align: center;
  border-top: 1px solid #f4f4f5;
}

.no-thanks-btn {
  padding: 0.6rem 1.5rem;
  border: 1px solid #d4d4d8;
  border-radius: 8px;
  background: white;
  color: #52525b;
  font-size: 0.8rem;
  font-weight: 500;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
}

.no-thanks-btn:hover {
  border-color: #71717a;
  color: #3f3f46;
}

/* Dark mode */
.dark .download-modal {
  background: #030712;
  border-color: #27272a;
}

.dark .no-thanks-btn {
  background: transparent;
  border-color: #3f3f46;
  color: #71717a;
}

.dark .no-thanks-btn:hover {
  border-color: #52525b;
  color: #a1a1aa;
}
</style>